import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganizationQuery } from '@/api/queries/organizations';
import { PATH } from '@/router/PATH';
import { ONBOARDING_FALLBACK } from '../../useOnboardingSession';

export type UseWelcomeStepResult = {
    organisationName: string;
    /** Start fresh: the main branch already exists, so straight to Done. */
    onStartFresh: () => void;
    /** Bring my catalog: Accounting first when the organisation needs it, else Branch. */
    onBringCatalog: () => void;
};

/**
 * Welcome's path choice. The organisation is the gate's cached read; until
 * it is there, Accounting counts as not needed (the gate renders nothing
 * before it, so that does not happen in practice).
 */
export const useWelcomeStep = (): UseWelcomeStepResult => {
    const navigate = useNavigate();
    const organisation = useOrganizationQuery().data;
    const accountingNeeded = organisation?.accountingStartNeeded ?? false;

    const onStartFresh = React.useCallback(() => {
        navigate(PATH.ONBOARDING_STEP.BIND('done'));
    }, [navigate]);

    const onBringCatalog = React.useCallback(() => {
        navigate(
            PATH.ONBOARDING_STEP.BIND(
                accountingNeeded ? 'accounting' : 'branch',
            ),
        );
    }, [accountingNeeded, navigate]);

    return {
        organisationName: organisation?.name.trim() || ONBOARDING_FALLBACK,
        onStartFresh,
        onBringCatalog,
    };
};
